<template>
  <div class="child-filter-node-ad">
    <FilterNodeItem
      v-for="(additionalCondition, index) in condition.additionalConditions"
      :key="index"
      :options="condition.selectOption(additionalCondition, isFirstNode)"
      :condition-type="additionalCondition.conditionType"
      @input="
        type => {
          onInputItem(type, index);
        }
      "
      @remove="onRemoveAdditionalCondition(index)"
    >
      <FilterLandingPageUrlField
        v-if="additionalCondition.conditionType === landingPageUrl"
        :condition="additionalCondition"
        @input="cnd => onInputAdditionalCondition(cnd, index)"
      />
      <FilterLandingPageTitleField
        v-if="additionalCondition.conditionType === landingPageTitle"
        :condition="additionalCondition"
        @input="cnd => onInputAdditionalCondition(cnd, index)"
      />
      <FilterDateHourField
        v-if="additionalCondition.conditionType === dateHour"
        :condition="additionalCondition"
        @input="cnd => onInputAdditionalCondition(cnd, index)"
      />
      <FilterPeriodField
        v-if="additionalCondition.conditionType === period"
        :condition="additionalCondition"
        @input="cnd => onInputAdditionalCondition(cnd, index)"
      />
    </FilterNodeItem>
    <FilterAddConditionButton
      v-if="condition.isConditionAddable"
      @click="onClickAddCondition"
    />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Model, Vue } from "vue-property-decorator";
import FilterNodeItem from "@/components/filter/FilterNodeItem.vue";
import FilterAddConditionButton from "@/components/filter/FilterAddConditionButton.vue";
import FilterLandingPageUrlField from "@/components/filter/form/FilterLandingPageUrlField.vue";
import FilterLandingPageTitleField from "@/components/filter/form/FilterLandingPageTitleField.vue";
import FilterDateHourField from "@/components/filter/form/FilterDateHourField.vue";
import FilterPeriodField from "@/components/filter/form/FilterPeriodField.vue";
import {
  ChildFilterNodeForAd,
  ChildFilterNodeForAdCondition
} from "@/models/search/filter-node/ChildFilterNodeForAd";
import { FilterNodeConditionType } from "@/models/search/filter-node-condition/FilterNodeCondition";
import { FilterLandingPageUrlCondition } from "@/models/search/filter-node-condition/FilterLandingPageUrlCondition";
import { FilterLandingPageTitleCondition } from "@/models/search/filter-node-condition/FilterLandingPageTitleCondition";
import { FilterAdditionalTimingCondition } from "@/models/search/filter-node-condition/FilterNodeCondition";
import { FilterFirstTimeCondition } from "@/models/search/filter-node-condition/FilterFirstTimeCondition";
import { FilterPeriodCondition } from "@/models/search/filter-node-condition/FilterPeriodCondition";
import { FilterDateHourCondition } from "@/models/search/filter-node-condition/FilterDateHourCondition";
import { MatchMethod } from "@/models/search/MatchMethod";

@Component({
  components: {
    FilterNodeItem,
    FilterLandingPageUrlField,
    FilterLandingPageTitleField,
    FilterDateHourField,
    FilterPeriodField,
    FilterAddConditionButton
  }
})
export default class ChildFilterNodeAd extends Vue {
  landingPageUrl = FilterNodeConditionType.LandingPageUrl;
  landingPageTitle = FilterNodeConditionType.LandingPageTitle;

  dateHour = FilterNodeConditionType.DateHour;
  period = FilterNodeConditionType.Period;

  sharedValues: { value: string; matchMethod: MatchMethod }[] = [];

  @Model("input", { type: ChildFilterNodeForAd, required: true })
  condition!: ChildFilterNodeForAd;

  @Prop({ type: Number, required: true })
  depth!: number;

  @Prop({ type: Boolean, required: true })
  isFirstNode!: boolean;

  mounted() {
    this.condition.additionalConditions.forEach((condition, index) => {
      if (condition instanceof FilterLandingPageUrlCondition) {
        this.sharedValues[index] = {
          value: condition.pageUrl,
          matchMethod: condition.matchMethod
        };
      } else if (condition instanceof FilterLandingPageTitleCondition) {
        this.sharedValues[index] = {
          value: condition.pageTitle,
          matchMethod: condition.matchMethod
        };
      }
    });
  }

  onInput(condition: ChildFilterNodeForAd) {
    this.$emit("input", condition);
  }

  onRemoveAdditionalCondition(index: number) {
    this.sharedValues.splice(index, 1);
    const additionalConditions = this.condition.additionalConditions.filter(
      (cnd, idx) => idx !== index
    );
    this.onInput(new ChildFilterNodeForAd(additionalConditions, this.depth));
  }

  onInputItem(type: FilterNodeConditionType, index: number) {
    let condition!:
      | FilterLandingPageUrlCondition
      | FilterLandingPageTitleCondition
      | FilterAdditionalTimingCondition;

    const sharedValue = this.sharedValues[index] || {
      value: "",
      matchMethod: MatchMethod.Partial
    };

    if (type === FilterNodeConditionType.LandingPageUrl) {
      condition = new FilterLandingPageUrlCondition(
        sharedValue.value,
        sharedValue.matchMethod
      );
    } else if (type === FilterNodeConditionType.LandingPageTitle) {
      condition = new FilterLandingPageTitleCondition(
        sharedValue.value,
        sharedValue.matchMethod
      );
    } else {
      this.sharedValues.splice(index, 1);

      if (type === FilterNodeConditionType.FirstTime) {
        condition = FilterFirstTimeCondition.buildDefaultData();
      } else if (type === FilterNodeConditionType.DateHour) {
        condition = FilterDateHourCondition.buildDefaultData();
      } else if (type === FilterNodeConditionType.Period) {
        condition = FilterPeriodCondition.buildDefaultData();
      }
    }

    const additionalConditions = this.condition.additionalConditions.map(
      (cnd, idx) => (idx === index ? condition : cnd)
    );
    this.onInput(new ChildFilterNodeForAd(additionalConditions, this.depth));
  }

  onInputAdditionalCondition(
    condition:
      | FilterLandingPageUrlCondition
      | FilterLandingPageTitleCondition
      | ChildFilterNodeForAdCondition,
    index: number
  ) {
    if (condition instanceof FilterLandingPageUrlCondition) {
      this.sharedValues[index] = {
        value: condition.pageUrl,
        matchMethod: condition.matchMethod
      };
    } else if (condition instanceof FilterLandingPageTitleCondition) {
      this.sharedValues[index] = {
        value: condition.pageTitle,
        matchMethod: condition.matchMethod
      };
    }

    const additionalConditions = this.condition.additionalConditions.map(
      (cnd, idx) => (idx === index ? condition : cnd)
    );
    this.onInput(new ChildFilterNodeForAd(additionalConditions, this.depth));
  }

  onClickAddCondition() {
    const additionalConditions = this.condition.additionalConditions;
    let condition!:
      | FilterLandingPageUrlCondition
      | FilterLandingPageTitleCondition
      | FilterFirstTimeCondition;
    if (!this.condition.hasLandingPageUrlCondition) {
      condition = new FilterLandingPageUrlCondition("", MatchMethod.Partial);
    } else if (!this.condition.hasLandingPageTitleCondition) {
      condition = new FilterLandingPageTitleCondition("", MatchMethod.Partial);
    } else {
      condition = new FilterFirstTimeCondition();
    }

    additionalConditions.push(condition);

    this.onInput(new ChildFilterNodeForAd(additionalConditions, this.depth));
  }
}
</script>
